<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js';
import { startPayment, checkNoonConflict } from '@/api/api';
import { getTotalPrice } from '@/services/price';
import { useBookingDataStore } from '@/stores/bookingData';
import { useBookingStepStore } from '@/stores/bookingStep';
import { useTextStore } from '@/stores/texts';
import { Steps } from '@/api/types/steps.enum';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { formatDate, nextDayStr } from '@/utils.ts/date';
import CouponCodes from '@/components/input/coupon-codes.vue';
import * as Sentry from '@sentry/vue';
import { analytics } from '@/analytics';
import { handOffToCheckout } from '@/services/checkoutHandoff';

const bookingData = useBookingDataStore();
const stepStore = useBookingStepStore();
const textStore = useTextStore();
const isPaymentLoading = ref(false);
const showErrorDialog = ref(false);
const errorMessage = ref('');
const isDateConflict = ref(false);
const showNoonDialog    = ref(false);
const noonDialogChecked = ref(false);
const noonConflict      = ref(false);
// Set when the embedding page ignored the Stripe hand-off: shown as a plain
// link to the same session instead of a pay button that spins forever.
const checkoutFallbackUrl = ref<string | null>(null);
// "Retour" stays clickable while the host page gets its chance: leaving the
// summary must not let the fallback send the customer to the old session.
let cancelCheckoutHandoff: (() => void) | null = null;
onBeforeUnmount(() => cancelCheckoutHandoff?.());

// The amount the customer would pay before any coupon — the discounted "Total à
// payer" shown below, in cents. Feeds the coupon validator and the €0 branch.
const totalCents = computed(() =>
  Math.round(parseFloat(
    getTotalPrice(bookingData.services, bookingData.foods, bookingData.drinks, bookingData.options, bookingData.discount)
      .replaceAll(',', '.'),
  ) * 100));

// Set once a coupon actually discounts the total, so the "new total" line shows
// the remaining due. Cleared when no coupon (or an invalidated one) applies.
const couponTotals = ref<{ discountCents: number; remainingDueCents: number } | null>(null);
const couponWidget = ref<InstanceType<typeof CouponCodes> | null>(null);
function onCouponUpdate(totals: { discountCents: number; remainingDueCents: number }) {
  couponTotals.value = totals.discountCents > 0 ? totals : null;
}

const TEXT_couponNewTotal = ref('Total après réduction');
const TEXT_checkoutFallback = ref('Continuer vers le paiement');
onMounted(async () => {
  TEXT_couponNewTotal.value = (await textStore.getText('coupon_new_total')) || TEXT_couponNewTotal.value;
  TEXT_checkoutFallback.value = (await textStore.getText('checkout_fallback_link')) || TEXT_checkoutFallback.value;
});

onMounted(async () => {
  // Already acknowledged earlier in this session (e.g. the customer went back
  // a step and returned) — don't re-run the check or re-block with the dialog.
  if (bookingData.noonAcknowledged) return;
  const hasLogement = bookingData.services.some(s => s.id === 'logement' && s.quantity > 0);
  if (hasLogement && bookingData.date) {
    const nextDay = nextDayStr(bookingData.date);
    // Scope to the lodging's side so a serre noon event never gates a
    // la salle/la table stay (and vice-versa).
    const result = await checkNoonConflict(nextDay, bookingData.venue || undefined);
    if (result?.success && result.data?.hasConflict) {
      noonConflict.value = true;
      showNoonDialog.value = true;
    } else if (!result?.success) {
      // Conflict state unknown — don't block the customer here; the backend
      // re-checks at payment time and pay() handles its 422 by opening the
      // acknowledgment dialog. Report so a flaky endpoint is visible.
      Sentry.captureException(result?.error ?? new Error('noon conflict check failed'));
    }
  }
});

function confirmNoonAcknowledgment() {
  bookingData.noonAcknowledged = true;
  showNoonDialog.value = false;
}

async function pay() {
  if (isPaymentLoading.value) {
    return;
  }

  isPaymentLoading.value = true;

  try {
    // Apply a code still sitting in the coupon field ("Ajouter" never clicked)
    // instead of silently paying full price. A rejected code stays on screen
    // with its error and payment stops.
    if (couponWidget.value && !(await couponWidget.value.applyPending())) {
      isPaymentLoading.value = false;
      return;
    }

    console.log('pay');
    // TEST
    // const stripe = await loadStripe('pk_test_51NTrbbHNVmO7iCyMEmgCMOsccolCaV15n7UcT1xRe7oWL1ZZfVfvJcz0X7p8ztvN7bKs7BX44su74aUl4FGhgBAE00y2IiTj6m');

    // PROD
    const stripe = await loadStripe('pk_live_51NTrbbHNVmO7iCyMbgoCQ2LsxwVeGmWSESREEAWCqbfjlG0q0xrhn4vA5MtjDNpNyF35FzO2DhyY84J5ZuM3fuzH00ALDn0vmk');

    if (stripe) {
      analytics.track('payment_started', { mode: String(stepStore.getMode()).toLowerCase(), venue: bookingData.venue || null });
      const r = await startPayment(
        formatDate(bookingData.date!),
        'diner',
        `${bookingData.clientInfo?.firstname} ${bookingData.clientInfo?.lastname}`,
        parseFloat(getTotalPrice(bookingData.services, bookingData.foods, bookingData.drinks, bookingData.options)),
        bookingData.getData(),
        bookingData.noonInviteToken ?? undefined,
        bookingData.noonAcknowledged || undefined,
        bookingData.appliedCouponCodes,
      );
      if (r?.success && r.data?.confirmed) {
        // A coupon fully covered the total — the backend confirmed the booking
        // without a Stripe session. Render the confirmation step directly.
        stepStore.freeBooking = true;
        stepStore.goToStep(Steps.Confirmation, true);
        return;
      }
      if (r?.success && r.data?.checkout_url) {
        // isPaymentLoading stays true: another click would create another draft
        // booking and Stripe session.
        const checkoutUrl = r.data.checkout_url;
        cancelCheckoutHandoff = handOffToCheckout(checkoutUrl, {
          onFallback: (topNavigation, error) => {
            checkoutFallbackUrl.value = checkoutUrl;
            // The iframe only sees the host's origin, never its page, and the
            // browser's error message names the live session URL: send neither.
            Sentry.captureMessage('Checkout hand-off ignored by the embedding page', {
              level: 'warning',
              tags: { checkout_handoff: 'fallback', top_navigation: topNavigation, mode: String(stepStore.getMode()).toLowerCase() },
              extra: {
                error_name: error instanceof Error ? error.name : undefined,
                visibility: document.visibilityState,
                effective_type: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType,
              },
            });
          },
        });
      } else {
        isPaymentLoading.value = false;
        // Backend backstop for the noon acknowledgment gate: if the mount-time
        // conflict check failed (or the conflict appeared after it ran), the
        // 422 arrives here — open the acknowledgment dialog instead of a
        // generic error so the customer can confirm and retry.
        if (r?.error?.status === 422 && (r.error.details as any)?.requiresNoonAcknowledgment) {
          noonConflict.value = true;
          showNoonDialog.value = true;
          return;
        }
        // A coupon that validated in the summary but changed state before
        // payment (redeemed/expired/voided elsewhere) comes back as a 422
        // coupon_invalid. Drop the stale codes, clear the adjusted total, and
        // ask the customer to re-enter rather than silently charging full price.
        if (r?.error?.status === 422 && (r.error.details as any)?.error === 'coupon_invalid') {
          couponTotals.value = null;
          bookingData.setCouponCodes([]);
          errorMessage.value = (await textStore.getText('coupon_err_state_changed'))
            || 'Un de vos codes n’est plus valable — veuillez le saisir à nouveau.';
          showErrorDialog.value = true;
          return;
        }
        isDateConflict.value = r?.error?.status === 409;
        if (r?.error) {
          errorMessage.value = r.error.details?.error || r.error.getUserMessage();
        } else {
          errorMessage.value = 'Une erreur inattendue est survenue. Veuillez réessayer.';
        }
        showErrorDialog.value = true;
      }


      /*const fetchClientSecret = async () => {
        console.log('start pay data', bookingData.getData());
        const r = await startPayment(formatDate(bookingData.date!), 'diner', `${bookingData.clientInfo?.firstname} ${bookingData.clientInfo?.lastname}`, parseFloat(getTotalPrice(bookingData.services, bookingData.foods, bookingData.drinks, bookingData.options)), bookingData.getData());
        if (r?.success && r.data) {
          return r.data.clientSecret
        }
        return null;
      }

      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret,
      });

      // Mount Checkout
      checkout.mount('#checkout');*/
    } else {
      isPaymentLoading.value = false;
    }
  } catch (error) {
    console.error('Payment error:', error);
    isPaymentLoading.value = false;
    isDateConflict.value = false;
    errorMessage.value = 'Une erreur inattendue est survenue. Veuillez réessayer.';
    showErrorDialog.value = true;
  }
}

function handleErrorDismiss() {
  showErrorDialog.value = false;
  if (isDateConflict.value) {
    stepStore.goToStep(Steps.Date, true);
  }
}
</script>

<template>
  <v-dialog v-model="showErrorDialog" persistent width="auto" scroll-strategy="none" class="dialog-viewport-center">
    <v-card max-width="500"
      :prepend-icon="isDateConflict ? 'mdi-calendar-remove' : 'mdi-alert-circle-outline'"
      :title="isDateConflict ? 'Date indisponible' : 'Erreur de paiement'"
      :text="errorMessage">
      <template v-slot:actions>
        <v-btn color="primary" @click="handleErrorDismiss">
          {{ isDateConflict ? 'Choisir une autre date' : 'Fermer' }}
        </v-btn>
      </template>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showNoonDialog" persistent width="auto" scroll-strategy="none" class="dialog-viewport-center">
    <v-card max-width="560" prepend-icon="mdi-home-clock-outline" title="Information importante concernant votre nuitée">
      <v-card-text>
        En raison d'une réservation prévue le lendemain à 12h30, le logement devra être libéré au
        plus tard à 10h30. Afin de vous permettre de profiter pleinement de votre séjour, le brunch
        sera prévu à 9h00 au plus tard.
        <br /><br />
        <v-checkbox
          v-model="noonDialogChecked"
          label="J'ai pris connaissance de cette information"
          hide-details
        />
      </v-card-text>
      <template v-slot:actions>
        <v-btn color="primary" :disabled="!noonDialogChecked" @click="confirmNoonAcknowledgment">
          Confirmer
        </v-btn>
      </template>
    </v-card>
  </v-dialog>
  <div class="bpa-front-tabs--panel-body __bpa-is-active eauget-booking-step-type" data-testid="step-summary-panel">
    <div class="bpa-front-default-card"><!---->
      <div class="bpa-front-dc--body"><!---->
        <div class="el-row">
          <div class="el-col el-col-24 el-col-xs-24 el-col-sm-24 el-col-md-24 el-col-lg-24 el-col-xl-24">
            <div class="bpa-front-module-container bpa-front-module--service" data-testid="booking-summary">
                <food-summary />
                <menu-summary v-if="bookingData.menu.length > 0" />
                <div class="bpa-front-module-heading">Allergies</div>
                <div class="allergy-summary">
                <p v-if="bookingData.clientInfo.allergy">{{ bookingData.clientInfo.allergy }}</p>
                <p v-else>Néant</p>
                <br />
                </div>
                <div v-if="bookingData.clientInfo.comment" class="bpa-front-module-heading">Remarques</div>
                <div v-if="bookingData.clientInfo.comment" class="allergy-summary">
                <p>{{ bookingData.clientInfo.comment }}</p>
                <br />
                </div>
                <drink-summary />
                <logement-summary />
                <options-summary />
                <info-summary />
                <br />
                <h2>Montant de la réservation</h2>
                <p>Total à payer:<span class="discounted-price" v-if="bookingData.discount > 0"> {{ getTotalPrice(bookingData.services, bookingData.foods, bookingData.drinks, bookingData.options) }} €</span> {{ getTotalPrice(bookingData.services, bookingData.foods, bookingData.drinks,
                  bookingData.options, bookingData.discount) }} €<span v-if="bookingData.discount > 0"> (-{{ bookingData.discount * 100 }}%)</span></p>
                <coupon-codes ref="couponWidget" :total-cents="totalCents" @update="onCouponUpdate" />
                <p v-if="couponTotals" data-testid="coupon-new-total">
                  {{ TEXT_couponNewTotal }}: {{ (couponTotals.remainingDueCents / 100).toFixed(2).replace('.', ',') }} €
                </p>
                <div id="checkout">
                  <!-- Checkout will insert the payment form here -->
                </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bpa-front-dc--footer">
        <div class="el-row">
          <div class="el-col el-col-24">
            <div class="bpa-front-tabs--foot"><!----><button type="button" @click="$emit('previousStep')" data-testid="btn-previous"
                class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--borderless el-button--default"><!----><!----><span><svg
                    xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" viewBox="0 0 24 24">
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M9.7,18.3L9.7,18.3c0.39-0.39,0.39-1.02,0-1.41L5.83,13H21c0.55,0,1-0.45,1-1v0c0-0.55-0.45-1-1-1H5.83l3.88-3.88 c0.39-0.39,0.39-1.02,0-1.41l0,0c-0.39-0.39-1.02-0.39-1.41,0L2.7,11.3c-0.39,0.39-0.39,1.02,0,1.41l5.59,5.59 C8.68,18.68,9.32,18.68,9.7,18.3z">
                    </path>
                  </svg>
                  Retour </span></button> <button type="button" @click="pay()"
                :disabled="isPaymentLoading || (noonConflict && !bookingData.noonAcknowledged)" data-testid="btn-pay"
                :class="['el-button', 'bpa-front-btn', 'bpa-front-btn__medium', 'bpa-front-btn--primary', 'el-button--default', 'eauget-next-step', { 'is-loading': isPaymentLoading }]"><!----><!----><span>
                  <v-progress-circular
                    v-if="isPaymentLoading && !checkoutFallbackUrl"
                    indeterminate
                    size="16"
                    width="2"
                    color="white"
                    class="mr-2"
                  ></v-progress-circular>
                  Suivant :&nbsp;<strong>Paiement</strong> <svg v-if="!isPaymentLoading" xmlns="http://www.w3.org/2000/svg"
                    enable-background="new 0 0 24 24" viewBox="0 0 24 24">
                    <rect fill="none" height="24" width="24"></rect>
                    <path
                      d="M14.29,5.71L14.29,5.71c-0.39,0.39-0.39,1.02,0,1.41L18.17,11H3c-0.55,0-1,0.45-1,1v0c0,0.55,0.45,1,1,1h15.18l-3.88,3.88 c-0.39,0.39-0.39,1.02,0,1.41l0,0c0.39,0.39,1.02,0.39,1.41,0l5.59-5.59c0.39-0.39,0.39-1.02,0-1.41L15.7,5.71 C15.32,5.32,14.68,5.32,14.29,5.71z">
                    </path>
                  </svg></span></button>
                <a v-if="checkoutFallbackUrl" :href="checkoutFallbackUrl" target="_top" data-testid="checkout-fallback-link"
                  class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default">{{ TEXT_checkoutFallback }}</a></div>
          </div>
        </div>
      </div>
      <div class="bpa-fm--service__advance-options">
        <div class="bpa-sao--body"><!----> <!----></div>
        <div class="bpa-sao--footer"><button type="button"
            class="el-button bpa-front-btn bpa-front-btn--borderless bpa-front-btn__medium el-button--default"><!----><!----><span>Annuler</span></button>
          <button type="button"
            class="el-button bpa-front-btn bpa-front-btn__medium bpa-front-btn--primary el-button--default"><!----><!----><span>Continuer</span></button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media only screen and (max-width: 576px) {
  .menu-summary, p, h2 {
    margin: 0px 15px !important;
  }
}

.discounted-price {
  text-decoration: line-through !important;
  color: red;
}

.is-loading {
  opacity: 0.7;
  cursor: not-allowed;
}

.mr-2 {
  margin-right: 8px;
}</style>
